/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

export interface IVisitor {
  visitTag(tag: Tag): any;
  visitText(text: Text): any;
  visitDeclaration(decl: Declaration): any;
  visitDoctype(doctype: Doctype): any;
}

class _Visitor implements IVisitor {
/**
 * @param {?} tag
 * @return {?}
 */
visitTag(tag: Tag): string {
    const /** @type {?} */ strAttrs = this._serializeAttributes(tag.attrs);

    if (tag.children.length == 0) {
      return `<${tag.name}${strAttrs}/>`;
    }

    const /** @type {?} */ strChildren = tag.children.map(node => node.visit(this));
    return `<${tag.name}${strAttrs}>${strChildren.join('')}</${tag.name}>`;
  }
/**
 * @param {?} text
 * @return {?}
 */
visitText(text: Text): string { return text.value; }
/**
 * @param {?} decl
 * @return {?}
 */
visitDeclaration(decl: Declaration): string {
    return `<?xml${this._serializeAttributes(decl.attrs)} ?>`;
  }
/**
 * @param {?} attrs
 * @return {?}
 */
private _serializeAttributes(attrs: {[k: string]: string}) {
    const /** @type {?} */ strAttrs = Object.keys(attrs).map((name: string) => `${name}="${attrs[name]}"`).join(' ');
    return strAttrs.length > 0 ? ' ' + strAttrs : '';
  }
/**
 * @param {?} doctype
 * @return {?}
 */
visitDoctype(doctype: Doctype): any {
    return `<!DOCTYPE ${doctype.rootTag} [\n${doctype.dtd}\n]>`;
  }
}

const /** @type {?} */ _visitor = new _Visitor();
/**
 * @param {?} nodes
 * @return {?}
 */
export function serialize(nodes: Node[]): string {
  return nodes.map((node: Node): string => node.visit(_visitor)).join('');
}

export interface Node { visit(visitor: IVisitor): any; }

export class Declaration implements Node {
public attrs: {[k: string]: string} = {};
/**
 * @param {?} unescapedAttrs
 */
constructor(unescapedAttrs: {[k: string]: string}) {
    Object.keys(unescapedAttrs).forEach((k: string) => {
      this.attrs[k] = _escapeXml(unescapedAttrs[k]);
    });
  }
/**
 * @param {?} visitor
 * @return {?}
 */
visit(visitor: IVisitor): any { return visitor.visitDeclaration(this); }
}

function Declaration_tsickle_Closure_declarations() {
 /** @type {?} */
Declaration.prototype.attrs;
}


export class Doctype implements Node {
/**
 * @param {?} rootTag
 * @param {?} dtd
 */
constructor(public rootTag: string,
public dtd: string){};
/**
 * @param {?} visitor
 * @return {?}
 */
visit(visitor: IVisitor): any { return visitor.visitDoctype(this); }
}

function Doctype_tsickle_Closure_declarations() {
 /** @type {?} */
Doctype.prototype.rootTag;
 /** @type {?} */
Doctype.prototype.dtd;
}


export class Tag implements Node {
public attrs: {[k: string]: string} = {};
/**
 * @param {?} name
 * @param {?=} unescapedAttrs
 * @param {?=} children
 */
constructor(
public name: string, unescapedAttrs: {[k: string]: string} = {},
public children: Node[] = []) {
    Object.keys(unescapedAttrs).forEach((k: string) => {
      this.attrs[k] = _escapeXml(unescapedAttrs[k]);
    });
  }
/**
 * @param {?} visitor
 * @return {?}
 */
visit(visitor: IVisitor): any { return visitor.visitTag(this); }
}

function Tag_tsickle_Closure_declarations() {
 /** @type {?} */
Tag.prototype.attrs;
 /** @type {?} */
Tag.prototype.name;
 /** @type {?} */
Tag.prototype.children;
}


export class Text implements Node {
  value: string;
/**
 * @param {?} unescapedValue
 */
constructor(unescapedValue: string) { this.value = _escapeXml(unescapedValue); };
/**
 * @param {?} visitor
 * @return {?}
 */
visit(visitor: IVisitor): any { return visitor.visitText(this); }
}

function Text_tsickle_Closure_declarations() {
 /** @type {?} */
Text.prototype.value;
}


export class CR extends Text {
/**
 * @param {?=} ws
 */
constructor(ws: number = 0) { super(`\n${new Array(ws + 1).join(' ')}`); }
}

const /** @type {?} */ _ESCAPED_CHARS: [RegExp, string][] = [
  [/&/g, '&amp;'],
  [/"/g, '&quot;'],
  [/'/g, '&apos;'],
  [/</g, '&lt;'],
  [/>/g, '&gt;'],
];
/**
 * @param {?} text
 * @return {?}
 */
function _escapeXml(text: string): string {
  return _ESCAPED_CHARS.reduce(
      (text: string, entry: [RegExp, string]) => text.replace(entry[0], entry[1]), text);
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
